import { cn, Container, Section } from '@resolute/ui';
import { ShieldAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Reveal } from '@/components/motion/reveal';
import { PageHero } from '@/components/page-hero';
import { Seo } from '@/lib/seo';

const PRIVACY_EMAIL = 'ronak@resoluteproperty.in';

const IMPORTANT_NOTICE =
  'Important: This Privacy Policy governs how Resolute Property Solution collects, uses, and protects your personal information. Please read it carefully before using our services.';

interface PolicySection {
  id: string;
  heading: string;
  paragraphs: string[];
}

const SECTIONS: PolicySection[] = [
  {
    id: 'introduction',
    heading: 'Introduction',
    paragraphs: [
      'This Privacy Policy sets out the terms of collection, use, and disclosure of information (including personal information) that may be collected by Resolute Property Solution from you at any time that you interact, including when you visit this website (Site), when you purchase or use any of the services available on the Site (Services), or when you call our sales or support associates.',
      'Unless contrary intention appears in the Terms of Service and any other Service Agreement, the Information collected/received from you shall be used and protected in accordance with the terms set out hereunder.',
    ],
  },
  {
    id: 'information-we-collect',
    heading: 'Information we collect',
    paragraphs: [
      'Unless otherwise stated in any other Service Agreement, the personal information collected during the process of registration or while filling up forms, if any, shall constitute personal information of the User which category shall include information such as your email id, first name, last name/ company name, zip, country, date of birth, gender, contact number, contact address, etc. (Personal Information) with legal entities within our group globally who will take steps to safeguard it in accordance with the Privacy Policy.',
      'We may collect information about your use of the Site, the Products and Services, including, but not limited to, information on the types of Services used, the type of internet browser or computer operating system you are using and the IP address of your computer.',
      'We may also collect certain information about your computer when you use the Site or any Services in order to facilitate, evaluate and verify your use of the Site and the Services.',
    ],
  },
  {
    id: 'how-we-use-your-information',
    heading: 'How we use your information',
    paragraphs: [
      'We may use your information to respond to your inquiries, provide customer service support, send you important information about the services, and send you marketing communications (with your consent) via different channels, including but not limited to SMS, Email, RCS, WhatsApp, and Voice with persons that we have a strategic relationship with or that perform work for us or assist in the maintenance, development, distribution, and improvement of the Services (including, but not limited to, companies that help us process information, extend credit, fulfill customer orders, deliver Services to you, etc.) to provide Services to you on our behalf, which persons are also obligated to protect your personal information in accordance with the Privacy Policy, except if we inform you otherwise at the time of collection.',
      'While you are allowing us to use your Information, you will always own all of your Information. You agree that we reserve the right to add such Information to our personal database for use as we may in our sole discretion deem fit.',
      'You acknowledge that besides your information, you may add certain additional information on the Site for the purpose of availing the Services (Additional Information). We may use Information and/or Additional Information provided by you in connection with Services availed by you or any analysis in respect of Services to provide, you and to other users of the Site or persons with whom we are associated, Services and for us to use the same to improve the offering of our services. We may also use your Information for internal operations, including troubleshooting, data analysis, testing and service improvement.',
    ],
  },
  {
    id: 'disclosure-of-your-information',
    heading: 'Disclosure of your information',
    paragraphs: [
      'Your Personal Information shall not be shared with any third party without your permission, provided that in the following cases your Personal Information may be shared without obtaining your prior consent.',
      'When required by applicable law or any order of a relevant authority or if we determine that such disclosure should be made for reasons of national security, law enforcement, or other issues of public importance.',
      'We will not share your Information with third parties unless we have received your permission or have given notice to you such as by telling you about it in this Privacy Policy or as required by applicable law or judgment or order of any relevant authority.',
      'If we, at any point, sell or transfer our assets or business (or the assets or business of a division or subsidiary) to another person or entity, or (or a division or subsidiary) is acquired by or merged with another entity, you agree that we may provide to such entity all Information that is related to that part of our assets or business that was sold to or merged with the other entity without obtaining your further consent.',
    ],
  },
  {
    id: 'marketing-communications',
    heading: 'Marketing communications',
    paragraphs: [
      'You accept that, from time to time, we may utilise your Personal Information for advertising and marketing purposes and we shall have the right to share such information with any of our affiliates or group entities (Group) for similar purposes. You shall have the option to unsubscribe from receipt of such emails or messages by giving notice to us of the same.',
    ],
  },
  {
    id: 'data-security',
    heading: 'Data security',
    paragraphs: [
      'We take protecting your privacy very seriously. To make sure your personal information is secure, we communicate these guidelines to our employees and strictly enforce privacy.',
      'You acknowledge and agree that internet transmissions are never completely private or secure. You understand that any message or information sent or provided to Resolute Property Solution may be read or intercepted by others, even if there is a special notice that a particular transmission is encrypted and that Resolute Property Solution shall have no liability whatsoever in the event of any such interception by or disclosure of such information to a third party.',
    ],
  },
  {
    id: 'international-data-transfer',
    heading: 'International data transfer',
    paragraphs: [
      'You agree and acknowledge that Information (including Personal Information) received from you may be transferred, even temporarily across national boundaries to different countries around the world within Resolute Property Solution group for analysis.',
    ],
  },
  {
    id: 'term-and-changes',
    heading: 'Term & changes to this policy',
    paragraphs: [
      'You acknowledge and agree that the Privacy Policy will be binding on you during the entire term of your use of any and/or all of the Services and the Site and shall survive any termination of your relationship with Resolute Property Solution or use of the Site.',
      'This Privacy Policy is subject to change at the sole discretion of Resolute Property Solution at any time without prior notice thereof.',
    ],
  },
  {
    id: 'relationship-with-other-terms',
    heading: 'Relationship with other terms',
    paragraphs: [
      'This Privacy Policy shall be read in conjunction with the Terms of Service and any other terms that govern the Services provided by Resolute Property Solution including any amendments thereto as may be in force from time to time. In case of any conflict between such terms the relevant terms and conditions shall be harmoniously constructed to uphold the interest of Resolute Property Solution.',
    ],
  },
];

/** Scroll-spy: highlights whichever section's heading is nearest the top of
 *  the viewport in the sticky table of contents. */
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState(ids[0]!);
  const refs = useRef<Record<string, Element | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-112px 0px -70% 0px', threshold: 0 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      refs.current[id] = el;
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return active;
}

export default function PrivacyPage() {
  const ids = SECTIONS.map((s) => s.id);
  const activeId = useActiveSection(ids);

  return (
    <>
      <Seo
        title="Privacy Policy"
        description="How Resolute Property Solution collects, uses, and protects your personal information."
        path="/privacy"
      />

      <PageHero
        eyebrow="Legal"
        title="Privacy Policy."
        titleLine2="Your data, protected."
        description="How we collect, use and protect your information when you visit this site or use our services."
        meta="Last updated: July 2026"
      />

      <Section>
        <Container>
          {/* No max-w cap here (unlike an earlier version) — a narrower cap on
              this grid left the whole two-column block stranded well short of
              Container's actual 1440px width, wasting space that never reached
              the content column. Matches Search/Property-Detail's grids, which
              also rely on Container alone. The content column's own max-w-4xl
              below is the deliberate readability cap on paragraph line length. */}
          <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
            {/* Table of contents — sticky, scroll-spy highlighted */}
            <aside className="hidden lg:block">
              <nav className="lg:sticky lg:top-[calc(var(--header-offset)+1.5rem)]" aria-label="Privacy Policy sections">
                <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  On this page
                </p>
                <ul className="mt-3 flex flex-col gap-0.5 border-l border-border">
                  {SECTIONS.map((s, i) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={cn(
                          '-ml-px block border-l-2 py-1.5 pl-3 text-sm transition-colors duration-200 ease-out-expo',
                          activeId === s.id
                            ? 'border-primary font-medium text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {i + 1}. {s.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            {/* Content — max-w-4xl caps paragraph line length for readability;
                narrower than the grid's actual 1fr track (now that the outer
                cap above is gone), so this is a deliberate reading-width
                choice, not an accidental leftover squeeze. Matches Terms. */}
            <div className="max-w-4xl">
              <Reveal className="mb-10 flex gap-3 rounded-lg border border-primary/25 bg-primary/5 p-5">
                <ShieldAlert className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-foreground">{IMPORTANT_NOTICE}</p>
              </Reveal>

              {/* Each section reveals independently as it's scrolled to — not a
                  manual index-proportional delay (which only "staggers" the
                  first screenful; sections further down would already be
                  fully revealed by the time they're actually scrolled into
                  view, since Reveal fires on ITS OWN viewport entry). */}
              <div className="space-y-10">
                {SECTIONS.map((s, i) => (
                  <div key={s.id} id={s.id} className="scroll-mt-24">
                    <Reveal>
                      <div className="flex items-start gap-3">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded border border-border bg-card text-sm font-semibold text-muted-foreground">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <h2 className="pt-0.5 text-lg font-semibold tracking-tight">{s.heading}</h2>
                      </div>
                      <div className="mt-3 space-y-3 pl-11">
                        {s.paragraphs.map((p) => (
                          <p key={p.slice(0, 40)} className="leading-relaxed text-muted-foreground">
                            {p}
                          </p>
                        ))}
                      </div>
                    </Reveal>
                  </div>
                ))}
              </div>

              <Reveal className="mt-10 rounded-lg border border-border bg-card p-6">
                <h2 className="text-lg font-semibold">Questions?</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Email{' '}
                  <a
                    href={`mailto:${PRIVACY_EMAIL}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {PRIVACY_EMAIL}
                  </a>{' '}
                  and we&apos;ll help. See also our{' '}
                  <Link to="/cookies" className="font-medium text-primary hover:underline">
                    Cookie Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms" className="font-medium text-primary hover:underline">
                    Terms of Use
                  </Link>
                  .
                </p>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
